import React, { useState } from 'react'
import { scroller } from 'react-scroll'

import {
  Container,
  Css,
  ExpandableButton,
  TextBody,
  TextTitle,
  Badge,
  ListContainer,
  VariantList,
  Icon,
  Tools
} from '@pdrj/pmi-iqos-ds-react'
import {
  Badges,
  BazaarVoice,
  htmlTranslation,
  PointsBubble
} from '@pdrj/pmi-iqos-shared-react'
import ProductCarousel from './product-carousel'

const imageBlockId = 'product-detail-images'
const actionBlockId = 'product-detail-actions'
const descriptionBlockId = 'product-detail-description'

const ProductDetailContent = ({
  translations,
  product,
  options,
  selectedOptionIndex,
  loading,
  bazaarVoiceReady,
  colorChangeHandler,
  backgroundColor,
  loadingButton,
  alternativeQoins,
  disclaimerImage,
  showVariants = true,
  showVariantName = true,
  children,
  mediaFullHeight = false,
  sizeSelector
}) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false)

  const handleReadMoreClick = () => {
    setIsDescriptionExpanded(!isDescriptionExpanded)
    if (isDescriptionExpanded) {
      scroller.scrollTo(actionBlockId, {
        duration: 0,
        smooth: true
      })
    }
  }

  const isReadMoreNecessary = () => {
    if (
      (loading && !loadingButton) ||
      typeof document === 'undefined' ||
      typeof window === 'undefined'
    ) {
      return false
    }
    const imageBlock = document.getElementById(imageBlockId)
    const actionBlock = document.getElementById(actionBlockId)
    const descriptionBlock = document.getElementById(descriptionBlockId)
    if (Tools.isDesktop()) {
      // Desktop
      return (
        imageBlock &&
        actionBlock &&
        descriptionBlock &&
        actionBlock.scrollHeight + descriptionBlock.scrollHeight >
          imageBlock.scrollHeight + 70 // Little additional margin to prevent read more for 1 line
      )
    }
    return (
      descriptionBlock &&
      descriptionBlock.scrollHeight > (window.innerHeight * 50) / 100
      // 50vh
    )
  }

  const getDescriptionHeight = () => {
    const imageBlock =
      typeof document !== 'undefined' && document.getElementById(imageBlockId)
    const actionBlock =
      typeof document !== 'undefined' && document.getElementById(actionBlockId)
    const diff =
      (imageBlock && imageBlock.scrollHeight) -
      (actionBlock && actionBlock.scrollHeight)
    return isDescriptionExpanded || !isReadMoreNecessary()
      ? 'auto'
      : ['50vh', '50vh', diff ? `calc(${diff}px - 2rem)` : '0']
  }

  const selectedOption = options && options[selectedOptionIndex]
  if (selectedOption) {
    const { images, description, media, optionThumbnailBg } = selectedOption
    const productThumbnailBg = product ? product.productThumbnailBg : ''
    const bazaarVoice = product ? product.bv : null
    const bazaarVoiceProductId = selectedOption
      ? selectedOption.bv_id
      : bazaarVoice
      ? bazaarVoice.bv_id
      : null
    const bazaarVoiceShowRating =
      bazaarVoice &&
      bazaarVoice.display_rating === true &&
      bazaarVoiceProductId &&
      bazaarVoiceReady
    return (
      <Container position={Container.positions.relative} pt={['0', '5', '0']}>
        {bazaarVoiceShowRating && <BazaarVoice.OverrideStyle />}
        <ListContainer columns={[1, 1, 2]} yBetween="6" xBetween="8">
          <Container fullWidth>
            <Container fullWidth id={imageBlockId}>
              <ProductCarousel
                images={images}
                mediaFullHeight={mediaFullHeight}
                videos={product.videos}
                prodImages={product.prodImages}
                media={media}
                productThumbnailBg={productThumbnailBg}
                optionThumbnailBg={optionThumbnailBg}>
                <Container
                  display={Container.displays.flex}
                  flexDirection={Container.flexDirections.column}
                  justifyContent={Container.justifyContentValues.between}
                  fullHeight>
                  <Container
                    display={Container.displays.flex}
                    justifyContent={Container.justifyContentValues.end}
                    pt="3"
                    pr="4"
                    zIndex="1">
                    {selectedOption.qoins ? (
                      <PointsBubble
                        headingLabel={
                          !alternativeQoins
                            ? translations.tk_pointsBadgeLottery
                            : translations.tk_pointsBadgeExchange
                        }
                        amount={selectedOption.qoins.toString()}
                        unitLabel={translations.tk_points}
                        alternative
                      />
                    ) : null}
                  </Container>
                  <Container
                    display={Container.displays.flex}
                    justifyContent={Container.justifyContentValues.start}
                    alignItems={Container.alignItemsValues.end}
                    pb="10"
                    pr="4"
                    pl={['4', '8']}
                    zIndex="1">
                    {disclaimerImage ? (
                      <TextBody
                        alignment={TextBody.alignments.left}
                        size={TextBody.sizes.s}
                        acceptsHtml
                        value={disclaimerImage}
                      />
                    ) : null}
                  </Container>
                </Container>
              </ProductCarousel>
            </Container>
          </Container>
          <Container
            itemid={options ? options[selectedOptionIndex].variantId : null}
            px={['0', '0', '24']}>
            <Css
              s={{
                overflow: 'hidden',
                position: 'relative',
                '::after': isReadMoreNecessary() && {
                  content: '""',
                  position: 'absolute',
                  height: '1rem',
                  bottom: 0,
                  left: '-1rem',
                  right: '-1rem',
                  boxShadow: isDescriptionExpanded
                    ? 'none'
                    : `inset ${backgroundColor} 0 -1rem 1rem`
                }
              }}>
              <Container id={actionBlockId}>
                {(product.family || selectedOption.badges) && (
                  <Badges
                    badges={
                      product.family
                        ? selectedOption.badges &&
                          selectedOption.badges.slice(0, 2)
                        : selectedOption.badges
                    }
                    testingPrefix="ProductDetail_Badge_ProductHint">
                    {product.family && (
                      <Container overflowHidden>
                        <Badge
                          text={product.family.name}
                          color={product.family.colour}
                          border
                          data-testing="ProductDetail_Badge_ProductFamily"
                        />
                      </Container>
                    )}
                  </Badges>
                )}
                <Container display={Container.displays.flex} pb="4">
                  <TextTitle
                    heading={TextTitle.headings.h3}
                    data-testing="ProductDetail_TextTitle_ProductName">
                    {product.name || translations.tk_productName}
                  </TextTitle>
                </Container>
                <Container pb="8">
                  {showVariantName &&
                    options[selectedOptionIndex].shortVariantName &&
                    options[selectedOptionIndex].shortVariantName.length >
                      0 && (
                      <Container pb="4">
                        <TextBody
                          size={TextBody.sizes.m}
                          data-testing="ProductDetail_TextBody_ShortVariantName">
                          {options[selectedOptionIndex].shortVariantName}
                        </TextBody>
                      </Container>
                    )}
                  {showVariants && (
                    <VariantList
                      variants={options}
                      selectedVariantIndex={selectedOptionIndex}
                      handleVariantIndexChange={colorChangeHandler}
                      data-testing-prefix="ProductDetail"
                    />
                  )}
                  {sizeSelector}
                </Container>
              </Container>
              {description && (
                <Container pb="8">
                  <Css
                    id={descriptionBlockId}
                    s={{
                      height: getDescriptionHeight(),
                      minHeight: '6rem',
                      ol: {
                        listStyle: 'auto !important',
                        padding: 'revert !important'
                      },
                      ul: {
                        listStyle: 'unset !important',
                        padding: 'revert !important'
                      }
                    }}>
                    <TextBody
                      acceptsHtml
                      value={description}
                      data-testing="ProductDetail_TextBody_Description"
                    />
                  </Css>
                </Container>
              )}
            </Css>
            {isReadMoreNecessary() && (
              <Container pt={isDescriptionExpanded ? '0' : '4'}>
                <ExpandableButton
                  onClickHandler={handleReadMoreClick}
                  icon={
                    isDescriptionExpanded
                      ? Icon.images.chevronUp
                      : Icon.images.chevronDown
                  }
                  iconPosition={ExpandableButton.iconPositions.right}
                  textTemplate={
                    <TextBody
                      size={TextBody.sizes.m}
                      {...htmlTranslation(
                        translations,
                        isDescriptionExpanded ? 'tk_readLess' : 'tk_readMore'
                      )}
                    />
                  }
                  data-testing="ProductDetail_Button_ReadMore"
                />
              </Container>
            )}
            {children}
          </Container>
        </ListContainer>
      </Container>
    )
  }
  return null
}

export default ProductDetailContent
